import {
  AlipayOutlined,
  CrownOutlined
} from '@ant-design/icons';
import {
  Alert,
  Button,
  Card,
  Col,
  InputNumber,
  Modal,
  Row,
  Space,
  Spin,
  Statistic,
  Tag,
  Typography,
  message
} from 'antd';
import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import alipayService from '../services/alipay';
import memberService from '../services/member';
import './BuyMembership.less';

const { Title: AntTitle, Text, Paragraph } = Typography;

const BuyMembership = () => {
  const { user, isAuthenticated, refreshUser } = useAuth();
  const [memberInfo, setMemberInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [wechatModalVisible, setWechatModalVisible] = useState(false);
  
  // 支付宝充值相关状态
  const [rechargeModalVisible, setRechargeModalVisible] = useState(false);
  const [rechargeAmount, setRechargeAmount] = useState(9.9);
  const [rechargePreview, setRechargePreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);

  // 加载会员信息
  useEffect(() => {
    if (isAuthenticated) {
      loadMemberInfo();
    }
  }, [isAuthenticated]);

  // 监听会员信息更新事件
  useEffect(() => {
    const handleMemberInfoUpdated = (event) => {
      setMemberInfo(event.detail.memberInfo);
    };

    window.addEventListener('member-info-updated', handleMemberInfoUpdated);
    return () => {
      window.removeEventListener('member-info-updated', handleMemberInfoUpdated);
    };
  }, []);

  const loadMemberInfo = async () => {
    setLoading(true);
    try {
      const info = await memberService.getMemberInfo();
      setMemberInfo(info);
    } catch (error) {
      console.error('加载会员信息失败:', error);
      message.error('加载会员信息失败');
    } finally {
      setLoading(false);
    }
  };

  // 显示微信二维码
  const showWechatQR = () => {
    setWechatModalVisible(true);
  };

  const handleWechatModalClose = () => {
    setWechatModalVisible(false);
  };

  // 支付宝充值相关函数
  const showRechargeModal = () => {
    setRechargeModalVisible(true);
    handlePreviewRecharge(rechargeAmount);
  };

  const handleRechargeModalClose = () => {
    setRechargeModalVisible(false);
    setRechargePreview(null);
  };

  const handlePreviewRecharge = async (amount) => {
    if (!amount || amount < 9.9) {
      message.warning('充值金额不能少于 9.9 元');
      return;
    }

    setLoadingPreview(true);
    try {
      const preview = await alipayService.previewRecharge(amount);
      setRechargePreview(preview);
    } catch (error) {
      console.error('预览充值失败:', error);
      message.error(error.response?.data?.detail || '预览充值失败');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleAmountChange = (value) => {
    setRechargeAmount(value);
    if (value && value >= 9.9) {
      handlePreviewRecharge(value);
    }
  };

  const handleSubmitPayment = async () => {
    if (!rechargeAmount || rechargeAmount < 9.9) {
      message.warning('充值金额不能少于 9.9 元');
      return;
    }

    setProcessingPayment(true);
    try {
      const order = await alipayService.createOrder(rechargeAmount, 'page');
      
      message.info('支付页面已打开，请在新窗口完成支付');
      
      alipayService.openPayment(order.payment_url);
      
      alipayService.pollOrderStatus(
        order.out_trade_no,
        async (orderStatus) => {
          message.success(`充值成功！获得 ${orderStatus.points_earned} 积分`);
          setProcessingPayment(false);
          setRechargeModalVisible(false);
          
          await loadMemberInfo();
          await refreshUser();
        },
        (error) => {
          setProcessingPayment(false);
          if (error.error === '查询超时') {
            message.warning('支付状态查询超时，请稍后在订单列表中查看');
          } else if (error.status === 'failed') {
            message.error('支付失败');
          } else if (error.status === 'cancelled') {
            message.info('支付已取消');
          }
        }
      );
      
    } catch (error) {
      console.error('创建支付订单失败:', error);
      message.error(error.response?.data?.detail || '创建支付订单失败');
      setProcessingPayment(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <Alert
          message="需要登录"
          description="请先登录以查看会员套餐"
          type="warning"
          showIcon
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 0' }}>
        <Spin size="large" />
        <div style={{ marginTop: '16px' }}>加载中...</div>
      </div>
    );
  }

  return (
    <div className="buy-membership-container">
      {/* 标题 */}
      <div style={{ marginBottom: '24px', textAlign: 'center' }}>
        <AntTitle level={2}>
          <CrownOutlined style={{ marginRight: '12px', color: '#faad14' }} />
          购买会员套餐
        </AntTitle>
        <Paragraph style={{ fontSize: '16px', color: '#666' }}>
          选择适合您的会员套餐，享受更多权益
        </Paragraph>
      </div>

      {/* 提示信息和快速充值 */}
      <Card
        style={{
          marginBottom: '24px',
          background: 'linear-gradient(135deg, #e6f7ff 0%, #f0f9ff 100%)',
          border: '1px solid #91d5ff',
          borderRadius: '12px'
        }}
        bodyStyle={{ padding: '20px 24px' }}
      >
        <Row align="middle" gutter={[24, 16]}>
          <Col xs={24} md="auto" flex="auto">
            <Alert 
              message="每处理一张化验单，消耗1积分，积分不足时，无法处理化验单。" 
              type="info" 
              showIcon
              style={{ border: 'none', background: 'transparent' }}
            />
          </Col>
          <Col xs={24} md="auto">
            <Space size="middle" style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button 
                type="primary" 
                size="large"
                icon={<AlipayOutlined />}
                onClick={showRechargeModal}
                style={{
                  height: '48px',
                  padding: '0 32px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(24, 144, 255, 0.3)'
                }}
              >
                在线充值
              </Button>
              <Button 
                size="large"
                onClick={showWechatQR}
                style={{
                  height: '48px',
                  padding: '0 24px',
                  fontSize: '16px',
                  borderRadius: '8px'
                }}
              >
                联系客服
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* 个人会员套餐 */}
      <div style={{ marginBottom: '24px' }}>
        <AntTitle level={3} style={{ marginBottom: '16px' }}>
          个人会员套餐
        </AntTitle>
        
        <Row gutter={[16, 16]}>
          {/* 普通用户 */}
          <Col xs={24} sm={12} md={6}>
            <Card
              className={`membership-card ${memberInfo?.member_level === 'normal' ? 'current' : ''}`}
              bodyStyle={{ padding: '24px 20px' }}
            >
              {memberInfo?.member_level === 'normal' && (
                <div className="current-badge">当前套餐</div>
              )}
              
              <div style={{ textAlign: 'center' }}>
                <AntTitle level={4} style={{ margin: '0 0 8px 0' }}>
                  普通用户
                </AntTitle>
                <div style={{ marginBottom: '16px' }}>
                  <Text style={{ fontSize: '32px', fontWeight: 'bold' }}>
                    免费
                  </Text>
                </div>
                
                <div style={{ textAlign: 'left', marginBottom: '24px' }}>
                  <div style={{ marginBottom: '8px', fontSize: '14px', color: '#666' }}>
                    包含功能：
                  </div>
                  <div style={{ marginBottom: '6px', fontSize: '13px' }}>
                    ✓ 单次最多处理 20 张化验单
                  </div>
                  <div style={{ marginBottom: '6px', fontSize: '13px' }}>
                    ✓ 新注册用户赠送价值 5 元的 10 积分
                  </div>
                  <div style={{ marginBottom: '6px', fontSize: '13px' }}>
                    ✓ 原价 0.5 元兑换 1 积分
                  </div>
                </div>
              </div>
            </Card>
          </Col>

          {/* 白卡会员 */}
          <Col xs={24} sm={12} md={6}>
            <Card
              className={`membership-card ${memberInfo?.member_level === 'white' ? 'current' : ''}`}
              bodyStyle={{ padding: '24px 20px' }}
            >
              {memberInfo?.member_level === 'white' && (
                <div className="current-badge">当前套餐</div>
              )}
              
              <div style={{ textAlign: 'center' }}>
                <AntTitle level={4} style={{ margin: '0 0 8px 0' }}>
                  白卡会员
                </AntTitle>
                <div style={{ marginBottom: '16px' }}>
                  <Text style={{ fontSize: '32px', fontWeight: 'bold' }}>
                    ¥9.9
                  </Text>
                  <Text style={{ fontSize: '14px', color: '#666' }}>/首次</Text>
                </div>
                
                <div style={{ textAlign: 'left', marginBottom: '24px', minHeight: '120px' }}>
                  <div style={{ marginBottom: '8px', fontSize: '14px', color: '#666' }}>
                    普通用户的所有功能，另外：
                  </div>
                  <div style={{ marginBottom: '6px', fontSize: '13px' }}>
                    ✓ 充值 25 积分（20+赠送5）
                  </div>
                  <div style={{ marginBottom: '6px', fontSize: '13px' }}>
                    ✓ 后续充值享受 95折 优惠
                  </div>
                  <div style={{ marginBottom: '6px', fontSize: '13px' }}>
                    ✓ 单次最多处理 2000 张化验单
                  </div>
                </div>
              </div>
            </Card>
          </Col>

          {/* 银卡会员 */}
          <Col xs={24} sm={12} md={6}>
            <Card
              className={`membership-card ${memberInfo?.member_level === 'silver' ? 'current' : ''}`}
              bodyStyle={{ padding: '24px 20px' }}
            >
              {memberInfo?.member_level === 'silver' && (
                <div className="current-badge">当前套餐</div>
              )}
              
              <div style={{ textAlign: 'center' }}>
                <AntTitle level={4} style={{ margin: '0 0 8px 0' }}>
                  银卡会员
                </AntTitle>
                <div style={{ marginBottom: '16px' }}>
                  <Text style={{ fontSize: '32px', fontWeight: 'bold' }}>
                    ¥199
                  </Text>
                  <Text style={{ fontSize: '14px', color: '#666' }}>/累计</Text>
                </div>
                
                <div style={{ textAlign: 'left', marginBottom: '24px', minHeight: '120px' }}>
                  <div style={{ marginBottom: '8px', fontSize: '14px', color: '#666' }}>
                    白卡会员的所有功能，另外：
                  </div>
                  <div style={{ marginBottom: '6px', fontSize: '13px' }}>
                    ✓ 充值享受 9折 优惠
                  </div>
                </div>
              </div>
            </Card>
          </Col>

          {/* 金卡会员 */}
          <Col xs={24} sm={12} md={6}>
            <Card
              className={`membership-card ${memberInfo?.member_level === 'gold' ? 'current' : ''}`}
              bodyStyle={{ padding: '24px 20px' }}
            >
              {memberInfo?.member_level === 'gold' && (
                <div className="current-badge">当前套餐</div>
              )}
              
              <div style={{ textAlign: 'center' }}>
                <AntTitle level={4} style={{ margin: '0 0 8px 0' }}>
                  金卡会员
                  <Tag color="orange" size="small" style={{ marginLeft: '8px' }}>推荐</Tag>
                </AntTitle>
                <div style={{ marginBottom: '16px' }}>
                  <Text style={{ fontSize: '32px', fontWeight: 'bold' }}>
                    ¥999
                  </Text>
                  <Text style={{ fontSize: '14px', color: '#666' }}>/累计</Text>
                </div>
                
                <div style={{ textAlign: 'left', marginBottom: '24px', minHeight: '120px' }}>
                  <div style={{ marginBottom: '8px', fontSize: '14px', color: '#666' }}>
                    银卡会员的所有功能，另外：
                  </div>
                  <div style={{ marginBottom: '6px', fontSize: '13px' }}>
                    ✓ 充值享受 8.5折 优惠
                  </div>
                </div>
              </div>
            </Card>
          </Col>
        </Row>

        {/* 高级会员套餐 */}
        <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
          {/* 钻石卡会员 */}
          <Col xs={24} md={12}>
            <Card
              className={`membership-card large ${memberInfo?.member_level === 'diamond' ? 'current' : ''}`}
              bodyStyle={{ padding: '24px' }}
            >
              {memberInfo?.member_level === 'diamond' && (
                <div className="current-badge">当前套餐</div>
              )}
              
              <Row>
                <Col span={12}>
                  <AntTitle level={4} style={{ margin: '0 0 8px 0' }}>
                    钻石卡会员
                  </AntTitle>
                  <div style={{ marginBottom: '16px' }}>
                    <Text style={{ fontSize: '32px', fontWeight: 'bold' }}>
                      ¥2999
                    </Text>
                    <Text style={{ fontSize: '14px', color: '#666' }}>/累计</Text>
                  </div>
                </Col>
                <Col span={12}>
                  <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>
                    金卡会员的所有功能，另外：
                  </div>
                  <div style={{ fontSize: '13px', marginBottom: '4px' }}>
                    ✓ 充值享受 8折 优惠
                  </div>
                </Col>
              </Row>
            </Card>
          </Col>

          {/* 至尊卡会员 */}
          <Col xs={24} md={12}>
            <Card
              className={`membership-card large ${memberInfo?.member_level === 'supreme' ? 'current' : ''}`}
              bodyStyle={{ padding: '24px' }}
            >
              {memberInfo?.member_level === 'supreme' && (
                <div className="current-badge">当前套餐</div>
              )}
              
              <Row>
                <Col span={12}>
                  <AntTitle level={4} style={{ margin: '0 0 8px 0' }}>
                    至尊卡会员
                  </AntTitle>
                  <div style={{ marginBottom: '16px' }}>
                    <Text style={{ fontSize: '32px', fontWeight: 'bold' }}>
                      ¥5999
                    </Text>
                    <Text style={{ fontSize: '14px', color: '#666' }}>/累计</Text>
                  </div>
                </Col>
                <Col span={12}>
                  <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>
                    钻石卡会员的所有功能，另外：
                  </div>
                  <div style={{ fontSize: '13px', marginBottom: '4px' }}>
                    ✓ 充值享受 7折 优惠
                  </div>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
      </div>

      {/* 企业合作 */}
      <Card
        style={{
          marginTop: '24px',
          marginBottom: '24px',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          border: 'none',
          borderRadius: '12px',
          color: 'white'
        }}
        bodyStyle={{ padding: '32px' }}
      >
        <Row align="middle" gutter={[16, 16]}>
          <Col xs={24} md={16}>
            <AntTitle level={3} style={{ color: 'white', margin: '0 0 8px 0' }}>
              企业合作
            </AntTitle>
            <Text style={{ color: 'rgba(255,255,255,0.9)', fontSize: '16px' }}>
              为企业客户提供定制化解决方案
            </Text>
            <div style={{ marginTop: '16px' }}>
              <div style={{ fontSize: '14px', marginBottom: '4px', color: 'rgba(255,255,255,0.8)' }}>
                ✓ 定制化功能开发
              </div>
              <div style={{ fontSize: '14px', marginBottom: '4px', color: 'rgba(255,255,255,0.8)' }}>
                ✓ 专业技术支持团队
              </div>
            </div>
          </Col>
          <Col xs={24} md={8} style={{ textAlign: 'center' }}>
            <div style={{ marginBottom: '16px' }}>
              <Text style={{ fontSize: '24px', fontWeight: 'bold', color: 'white' }}>
                企业定制
              </Text>
            </div>
            <Button 
              type="primary" 
              size="large"
              style={{ 
                background: 'rgba(255,255,255,0.2)', 
                borderColor: 'rgba(255,255,255,0.3)',
                borderRadius: '6px',
                fontWeight: 'bold'
              }}
              onClick={showWechatQR}
            >
              联系官方微信
            </Button>
          </Col>
        </Row>
      </Card>

      {/* 会员服务 */}
      <Card 
        title={
          <Space>
            <CrownOutlined style={{ color: '#faad14' }} />
            <span>会员服务</span>
          </Space>
        } 
        style={{ 
          marginBottom: '24px',
          background: 'linear-gradient(135deg, #f6f8fa 0%, #ffffff 100%)',
          border: '1px solid #e8e8e8'
        }}
      >
        <Row gutter={[24, 24]} align="middle">
          <Col xs={24} md={14}>
            <div style={{ padding: '24px 20px' }}>
              <div style={{ marginBottom: '20px' }}>
                <AntTitle level={4} style={{ 
                  color: '#1890ff', 
                  marginBottom: '8px',
                  fontSize: '18px'
                }}>
                  🎯 联系客服
                </AntTitle>
                <Text style={{ 
                  fontSize: '14px', 
                  color: '#666',
                  lineHeight: '1.6'
                }}>
                  如需充值积分或其他服务，请扫描右侧二维码添加客服微信
                </Text>
              </div>
              
              <div style={{ 
                background: '#f0f9ff',
                border: '1px solid #bae7ff', 
                borderRadius: '8px',
                padding: '16px'
              }}>
                <Space direction="vertical" size={8}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ 
                      display: 'inline-block',
                      width: '8px',
                      height: '8px',
                      background: '#52c41a',
                      borderRadius: '50%'
                    }}></span>
                    <Text style={{ fontSize: '13px', color: '#333' }}>
                      <strong>积分充值</strong> - 获取更多处理次数
                    </Text>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ 
                      display: 'inline-block',
                      width: '8px',
                      height: '8px',
                      background: '#1890ff',
                      borderRadius: '50%'
                    }}></span>
                    <Text style={{ fontSize: '13px', color: '#333' }}>
                      <strong>技术支持</strong> - 专业客服在线解答
                    </Text>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ 
                      display: 'inline-block',
                      width: '8px',
                      height: '8px',
                      background: '#faad14',
                      borderRadius: '50%'
                    }}></span>
                    <Text style={{ fontSize: '13px', color: '#333' }}>
                      <strong>会员服务</strong> - 享受专属权益
                    </Text>
                  </div>
                </Space>
              </div>
            </div>
          </Col>
          <Col xs={24} md={10}>
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center',
              justifyContent: 'center',
              padding: '24px 20px'
            }}>
              <div style={{ 
                position: 'relative',
                padding: '16px',
                background: '#fff',
                borderRadius: '16px',
                boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                border: '2px solid #f0f0f0'
              }}>
                <img 
                  src="/wechat-qrcode.png" 
                  alt="微信二维码" 
                  style={{ 
                    width: '200px', 
                    height: '200px',
                    borderRadius: '12px',
                    display: 'block'
                  }}
                />
                <div style={{
                  position: 'absolute',
                  top: '-8px',
                  right: '-8px',
                  background: '#52c41a',
                  color: '#fff',
                  borderRadius: '50%',
                  width: '24px',
                  height: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  boxShadow: '0 2px 8px rgba(82,196,26,0.4)'
                }}>
                  ✓
                </div>
              </div>
              <div style={{ 
                fontSize: '12px', 
                color: '#999', 
                marginTop: '4px'
              }}>
                MedSyntix官方客服
              </div>
            </div>
          </Col>
        </Row>
      </Card>

      {/* 支付宝充值弹窗 */}
      <Modal
        title={
          <Space>
            <AlipayOutlined style={{ color: '#1890ff' }} />
            <span>支付宝在线充值</span>
          </Space>
        }
        open={rechargeModalVisible}
        onCancel={handleRechargeModalClose}
        footer={[
          <Button key="cancel" onClick={handleRechargeModalClose}>
            取消
          </Button>,
          <Button 
            key="submit" 
            type="primary" 
            onClick={handleSubmitPayment}
            loading={processingPayment}
            icon={<AlipayOutlined />}
          >
            {processingPayment ? '处理中...' : '去支付'}
          </Button>
        ]}
        width={600}
        centered
      >
        <div style={{ padding: '20px 0' }}>
          {/* 充值金额输入 */}
          <div style={{ marginBottom: '24px' }}>
            <div style={{ marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
              充值金额（元）
            </div>
            <InputNumber
              value={rechargeAmount}
              onChange={handleAmountChange}
              min={9.9}
              step={10}
              precision={2}
              style={{ width: '100%' }}
              size="large"
              placeholder="请输入充值金额，最少 9.9 元"
            />
            <div style={{ marginTop: '8px', fontSize: '12px', color: '#999' }}>
              💡 单次充值最少 9.9 元
            </div>
          </div>

          {/* 快捷充值金额 */}
          <div style={{ marginBottom: '24px' }}>
            <div style={{ marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
              快捷金额
            </div>
            <Space wrap>
              {[9.9, 50, 100, 199, 500, 999].map((amount) => (
                <Button 
                  key={amount}
                  onClick={() => handleAmountChange(amount)}
                  type={rechargeAmount === amount ? 'primary' : 'default'}
                >
                  ¥{amount}
                </Button>
              ))}
            </Space>
          </div>

          {/* 充值预览 */}
          {loadingPreview ? (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <Spin tip="计算中..." />
            </div>
          ) : rechargePreview ? (
            <Card 
              style={{ 
                background: '#f6ffed', 
                border: '1px solid #b7eb8f',
                marginBottom: '16px'
              }}
            >
              <div style={{ marginBottom: '16px' }}>
                <AntTitle level={5} style={{ margin: 0, color: '#52c41a' }}>
                  充值预览
                </AntTitle>
              </div>
              
              <Row gutter={16}>
                <Col span={12}>
                  <Statistic
                    title="充值金额"
                    value={rechargePreview.amount}
                    prefix="¥"
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title="获得积分"
                    value={rechargePreview.total_points}
                    suffix="分"
                    valueStyle={{ color: '#faad14' }}
                  />
                </Col>
              </Row>

              {rechargePreview.bonus_points > 0 && (
                <Alert
                  message={`包含奖励积分 ${rechargePreview.bonus_points} 分`}
                  type="success"
                  showIcon
                  style={{ marginTop: '16px' }}
                />
              )}

              {rechargePreview.level_upgraded && (
                <Alert
                  message={
                    <span>
                      会员等级升级：
                      <Tag color="default" style={{ marginLeft: '8px' }}>
                        {rechargePreview.old_level_name}
                      </Tag>
                      <span style={{ margin: '0 8px' }}>→</span>
                      <Tag color="success">
                        {rechargePreview.new_level_name}
                      </Tag>
                    </span>
                  }
                  type="info"
                  showIcon
                  style={{ marginTop: '8px' }}
                />
              )}

              {/* 计算公式 */}
              {rechargePreview.calculation_formula && (
                <div className="calculation-formula">
                  <div className="formula-content">
                    {rechargePreview.calculation_formula.split('\n').map((line, index) => (
                      <div 
                        key={index}
                        className={`formula-line ${line.startsWith('【') ? 'header' : 'content'}`}
                      >
                        {line}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          ) : null}

          {/* 支付说明 */}
          <Alert
            message="支付说明"
            description={
              <div>
                <div style={{ marginBottom: '4px' }}>
                  1. 点击"去支付"后将打开支付宝支付页面
                </div>
                <div style={{ marginBottom: '4px' }}>
                  2. 请在新窗口完成支付，支付完成后系统自动充值积分
                </div>
                <div>
                  3. 如遇到问题，请联系客服处理
                </div>
              </div>
            }
            type="info"
            showIcon
          />
        </div>
      </Modal>

      {/* 微信二维码弹窗 */}
      <Modal
        title={
          <div style={{ textAlign: 'center' }}>
            <Space>
              <span style={{ fontSize: '18px' }}>📱</span>
              <span>联系官方微信</span>
            </Space>
          </div>
        }
        open={wechatModalVisible}
        onCancel={handleWechatModalClose}
        footer={null}
        width={400}
        centered
      >
        <div style={{ padding: '20px 0', textAlign: 'center' }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            marginBottom: '20px' 
          }}>
            <img 
              src="/wechat-qrcode.png" 
              alt="微信二维码" 
              style={{ 
                width: '200px', 
                height: '200px',
                border: '1px solid #f0f0f0',
                borderRadius: '8px',
                padding: '10px',
                background: '#fff'
              }}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <AntTitle level={5} style={{ margin: '0 0 8px 0', color: '#1890ff' }}>
              扫码添加官方微信
            </AntTitle>
            <Text style={{ color: '#666', fontSize: '14px' }}>
              专业客服为您提供：
            </Text>
          </div>

          <div style={{ textAlign: 'left', margin: '0 auto', maxWidth: '280px' }}>
            <div style={{ marginBottom: '8px', fontSize: '13px', color: '#333' }}>
              ✅ 会员充值服务
            </div>
            <div style={{ marginBottom: '8px', fontSize: '13px', color: '#333' }}>
              ✅ 技术支持与咨询
            </div>
            <div style={{ marginBottom: '8px', fontSize: '13px', color: '#333' }}>
              ✅ 企业合作洽谈
            </div>
            <div style={{ marginBottom: '8px', fontSize: '13px', color: '#333' }}>
              ✅ 定制化解决方案
            </div>
          </div>

          <div style={{ 
            marginTop: '20px', 
            padding: '12px', 
            background: '#f6ffed', 
            border: '1px solid #b7eb8f',
            borderRadius: '6px'
          }}>
            <Text style={{ fontSize: '12px', color: '#52c41a' }}>
              💡 工作时间：周一至周五 9:00-18:00
            </Text>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default BuyMembership;
